import { getGuestIdBucketNumber } from '@core/abTesting/abTesting'
import type { Api_Video_Poster } from '@core/api'
import { getGuestId } from '@core/tracking/guestId'

const DEFAULT_POSTER_WEIGHT = 1
/** `getGuestIdBucketNumber` returns a number between 0 and 99. */
const BUCKET_COUNT = 100

const getPosterWeight = (poster: Api_Video_Poster) =>
  Math.max(poster.weight ?? DEFAULT_POSTER_WEIGHT, 0)

const getHighestWeightPoster = (posters: Api_Video_Poster[]) =>
  posters.reduce((best, poster) =>
    getPosterWeight(poster) > getPosterWeight(best) ? poster : best,
  )

/**
 * Bucket of the current visitor, stable across renders and visits. Undefined
 * during SSR, where reading the guest id would create one shared by every
 * request, and for guest ids that are not hashable (custom `guest_id_override`).
 */
function getGuestBucket(): number | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }
  try {
    return getGuestIdBucketNumber(getGuestId())
  } catch {
    return undefined
  }
}

/**
 * Picks one of the eligible posters, treating `weight` as the relative
 * probability of that poster being rendered. A missing weight counts as 1, a
 * weight of 0 loses against any other weighted poster.
 *
 * The pick is bucketed by guest id instead of being random, so a visitor keeps
 * seeing the poster they were served before. Falls back to the highest weight
 * when no bucket is available.
 */
export function pickPosterByWeight(
  posters: Api_Video_Poster[] | undefined,
  isEligible: (poster: Api_Video_Poster) => boolean,
): Api_Video_Poster | undefined {
  const eligible = posters?.filter(isEligible) ?? []
  if (eligible.length < 2) {
    return eligible[0]
  }

  const totalWeight = eligible.reduce(
    (total, poster) => total + getPosterWeight(poster),
    0,
  )
  const bucket = getGuestBucket()

  if (!totalWeight || bucket === undefined) {
    return getHighestWeightPoster(eligible)
  }

  // Buckets are spread over the cumulative weights, e.g. with weights 30 and 70
  // buckets 0-29 get the first poster and buckets 30-99 the second one.
  const threshold = (bucket / BUCKET_COUNT) * totalWeight
  let cumulativeWeight = 0

  return (
    eligible.find((poster) => {
      cumulativeWeight += getPosterWeight(poster)
      return cumulativeWeight > threshold
    }) ?? eligible[eligible.length - 1]
  )
}
